import React from 'react';

const SuperAdminUsers = ({ superAdmins, form, setForm, isSaving, onSave }) => {
  const [errors, setErrors] = React.useState({});

  const validateForm = () => {
    const newErrors = {};
    
    // Username validation
    if (!form.user_name.trim()) {
      newErrors.user_name = 'Username is required';
    } else if (form.user_name.trim().length < 3) {
      newErrors.user_name = 'Username must be at least 3 characters';
    }
    
    // Password validation
    if (!form.password) {
      newErrors.password = 'Password is required';
    } else if (form.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    // Confirm password validation
    if (!form.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (form.password !== form.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    // Phone validation (optional but with format check if provided)
    if (form.phone && form.phone.trim()) {
      const phoneRegex = /^[0-9]{10,15}$/;
      if (!phoneRegex.test(form.phone.trim().replace(/[^0-9]/g, ''))) {
        newErrors.phone = 'Please enter a valid phone number (10-15 digits)';
      }
    }
    
    // Email validation (optional but with format check if provided)
    if (form.email && form.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave();
    } else {
      // Show a summary toast message
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        // You can also show toast here if you have toast configured
        alert(errorMessages[0]); // Simple alert for now
      }
    }
  };

  return (
    <section className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <div className="rounded-lg bg-white p-6 shadow-xl">
        <div className="text-xs uppercase tracking-[0.22em] text-cyan-700">New super admin</div>
        <h3 className="mt-2 text-2xl font-semibold text-slate-950">Create login</h3>
        <p className="mt-2 text-sm leading-6 text-slate-500">
          Add another platform-level login for managing packages, companies, and super admins.
        </p>

        <div className="mt-6 space-y-4">
          <label className="block space-y-2 text-sm font-medium text-slate-700">
            <span>Username <span className="text-red-500">*</span></span>
            <input
              className={`w-full rounded-lg border ${errors.user_name ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`}
              value={form.user_name}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, user_name: e.target.value }));
                if (errors.user_name) setErrors({ ...errors, user_name: '' });
              }}
              placeholder="superadmin2"
            />
            {errors.user_name && (
              <p className="mt-1 text-xs text-red-500">{errors.user_name}</p>
            )}
          </label>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block space-y-2 text-sm font-medium text-slate-700">
              <span>Password <span className="text-red-500">*</span></span>
              <input
                type="password"
                className={`w-full rounded-lg border ${errors.password ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`}
                value={form.password}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, password: e.target.value }));
                  if (errors.password) setErrors({ ...errors, password: '' });
                  if (errors.confirm_password) setErrors({ ...errors, confirm_password: '' });
                }}
                placeholder="Minimum 6 characters"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </label>
            <label className="block space-y-2 text-sm font-medium text-slate-700">
              <span>Confirm password <span className="text-red-500">*</span></span>
              <input
                type="password"
                className={`w-full rounded-lg border ${errors.confirm_password ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`}
                value={form.confirm_password}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, confirm_password: e.target.value }));
                  if (errors.confirm_password) setErrors({ ...errors, confirm_password: '' });
                }}
                placeholder="Repeat password"
              />
              {errors.confirm_password && (
                <p className="mt-1 text-xs text-red-500">{errors.confirm_password}</p>
              )}
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block space-y-2 text-sm font-medium text-slate-700">
              <span>Phone <span className="text-slate-400 text-xs">(Optional)</span></span>
              <input
                className={`w-full rounded-lg border ${errors.phone ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`}
                value={form.phone}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, phone: e.target.value }));
                  if (errors.phone) setErrors({ ...errors, phone: '' });
                }}
                placeholder="Optional"
              />
              {errors.phone && (
                <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
              )}
            </label>
            <label className="block space-y-2 text-sm font-medium text-slate-700">
              <span>Email <span className="text-slate-400 text-xs">(Optional)</span></span>
              <input
                type="email"
                className={`w-full rounded-lg border ${errors.email ? 'border-red-500' : 'border-slate-200'} px-4 py-3 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100`}
                value={form.email}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, email: e.target.value }));
                  if (errors.email) setErrors({ ...errors, email: '' });
                }}
                placeholder="Optional"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </label>
          </div>

          <button
            type="button"
            disabled={isSaving}
            onClick={handleSave}
            className="w-full rounded-lg bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? 'Creating...' : 'Create Super Admin'}
          </button>
        </div>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-xl">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-cyan-700">Platform logins</div>
            <h3 className="mt-2 text-2xl font-semibold text-slate-950">Super admins</h3>
          </div>
          <div className="text-sm text-slate-500">{superAdmins.length} database users</div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Username</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {superAdmins.map((admin) => (
                <tr key={admin.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-semibold text-slate-900">{admin.user_name}</td>
                  <td className="px-4 py-3 text-slate-600">{admin.email_id || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{admin.phone || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{admin.created_date || '-'}</td>
                </tr>
              ))}
              {superAdmins.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-slate-500">
                    No database super admins yet. The hard-coded fallback still works.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default SuperAdminUsers;